/**
 * Edit in place.
 *
 * Replaces the bubble with a composer seeded from the message. `requireDirty` keeps
 * Save disabled until something actually changed, so an accidental open-and-close is
 * not a no-op PATCH that bumps `updatedAt` and paints "(Edited)" on an untouched
 * message.
 *
 * ## The `uploads` contract, which is easy to get backwards
 *
 * Sending `uploads` REPLACES the whole attachment set; omitting it leaves attachments
 * untouched. V4's GraphQL default of `[]` turned "I only fixed a typo" into "delete
 * every attachment". This component therefore always seeds the composer with the
 * existing attachments and always sends the full resulting list.
 *
 * Voice notes ride that same list. They seed into the composer's `audios` rather than
 * its `attachments` (`toComposerUploads`) so the editor plays them instead of drawing a
 * file tile, and Save concatenates the two back into one `uploads` — so an edit that
 * only fixes a typo re-sends every recording, and the recorder, on when the surface
 * allows it, appends to the same set.
 *
 * ## The quote (internal comments only)
 *
 * A comment's editor shows the quote it was posted with; ✕ drops it and Reply on another
 * message swaps it (`use-channel-message-interactions`). The quote lives outside the form,
 * so a quote-only change lifts `requireDirty` itself, and Save sends `quotedMessageId`
 * only when the quote changed — omitted on the wire means "unchanged".
 */

import { toast } from "@birdwatch/ui/toast";
import { Button } from "@/components/ui/button/button";
import { BwCheckmarkLine, BwCloseLine } from "@/components/ui/icons";
import { SendButton } from "@/components/ui/message";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  ChatComposer,
  type ChatComposerValues,
  useChatComposerForm,
} from "@/features/shared/chat-composer";
import type {
  ChannelMessage,
  ChannelQuotedMessage,
} from "../_api/channel-endpoints";
import { useChannelMessageMutations } from "../_api/use-channel-message-mutations";
import { renderComposerSlot } from "../_libs/composer-slot";
import { toComposerUploads } from "../_libs/message-attachments";
import {
  canEditQuote,
  isQuoteChanged,
  toQuotePatch,
} from "../_libs/quoted-message";
import type {
  ChannelComposerConfig,
  ResolvedChannelSurface,
} from "../_types/channel-surface";
import { ChannelQuoteCard } from "./channel-quote-card";

type ChannelMessageEditProps = {
  cid: string;
  message: ChannelMessage;
  onDone: () => void;
  /**
   * The surface being edited in. Two things come off it and both are load-bearing:
   *
   * - `mentions` — the SAME extensions the create composer uses. Without them the
   *   Tiptap schema has no Mention node, so a stored `@[Name](userId:…)` seeds as
   *   plain text and saving destroys every mention in the message.
   * - `composer.editLeadingActions` — the attachment picker. It is a separate slot
   *   from `leadingActions` because that one also carries decorations (visit quick
   *   actions) that make no sense while editing.
   */
  surface: ResolvedChannelSurface;
  /** The quote Save writes — seeded from the message by the caller; ignored off comments. */
  quote: ChannelQuotedMessage | null;
  onQuoteChange: (quote: ChannelQuotedMessage | null) => void;
};

export function ChannelMessageEdit({
  cid,
  message,
  onDone,
  surface,
  quote,
  onQuoteChange,
}: ChannelMessageEditProps) {
  const { editMessage } = useChannelMessageMutations(cid);
  const config: ChannelComposerConfig =
    surface.composer === false ? {} : surface.composer;
  const isQuoteEditable = canEditQuote(cid);
  const quoteChanged =
    isQuoteEditable && isQuoteChanged(message.quotedMessage, quote);

  const uploads = toComposerUploads(message);
  const initialValues: ChatComposerValues = {
    content: message.body ?? "",
    attachments: uploads.attachments,
    audios: uploads.audios,
  };

  const composer = useChatComposerForm({
    source: { kind: "values", values: initialValues },
    errorTitle: "Can't update message!",
    // The surface's reasons, as for a new message: the homeowner thread refuses an
    // edit that still has a `[placeholder: …]`, and its reason names each blank.
    errorDescription: config.sendErrorDescription,
    maxCharacters: surface.maxCharacters,
    // A changed quote is a change the form cannot see.
    requireDirty: !quoteChanged,
    onSend: async (values) => {
      await editMessage(message.id, {
        body: values.content,
        uploads: [
          ...values.attachments.map((attachment) => attachment.id),
          ...values.audios.map((audio) => audio.id),
        ],
        ...(isQuoteEditable ? toQuotePatch(message.quotedMessage, quote) : {}),
      });
      toast.success({ title: "Message updated!" });
      onDone();
    },
  });

  return (
    <div className="px-3 py-3 xl:px-4">
      <ChatComposer
        composer={composer}
        // The card reads as FOCUSED for as long as it is open — a darker border and a
        // shadow lift it off the thread, so the row being edited is unmistakable against
        // the bubbles around it. Both win over the composer's defaults: `ChatComposer`
        // applies this last in its `cn(...)`.
        className="text-sm border-border-dark-high shadow-md"
        placeholder="Edit message..."
        innerHeader={
          isQuoteEditable && quote ? (
            <div className="p-2 pb-0">
              <ChannelQuoteCard
                quote={quote}
                titlePrefix="Reply to"
                onDismiss={() => onQuoteChange(null)}
              />
            </div>
          ) : null
        }
        extensions={surface.mentions}
        showMentionButton={!!surface.mentions}
        showAudioRecorder={!!config.audio && surface.attachments}
        enableFileDrop={surface.attachments}
        leadingActions={
          surface.attachments
            ? renderComposerSlot(config.editLeadingActions, composer)
            : null
        }
        // Cancel belongs next to Save on the right, and `trailingActions` renders in
        // the LEFT cluster — so both controls ride the send slot.
        sendSlot={({ requestSend, isSubmitting, isDisabled }) => (
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  kind="outline"
                  variant="neutral"
                  size="icon-md"
                  aria-label="Cancel"
                  onClick={onDone}
                >
                  <BwCloseLine />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Cancel</TooltipContent>
            </Tooltip>
            <SendButton
              icon={<BwCheckmarkLine />}
              size="icon-md"
              // Confirming an edit is not sending a message, and the design says so in
              // colour: green, not the composer's primary. `disabled:` still greys it out —
              // the variant outranks this base-layer background.
              className="bg-bg-success-max hover:bg-bg-success-max-hover"
              tooltipDescription="Save changes"
              onClick={requestSend}
              isLoading={isSubmitting}
              disabled={isDisabled}
            />
          </div>
        )}
      />
    </div>
  );
}
